import { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  usePurchaseOrderById,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useSubmitPO,
  useSupplierSearch,
} from '@hooks/usePurchasing';
import { useProductSearch } from '@hooks/useDictionaries';
import type {
  CreatePurchaseOrderRequest,
  PurchaseOrderLineInput,
} from '@/types/purchasing';

interface PurchaseOrderEditPageProps {
  orderId?: string;
  onBack: () => void;
}

interface OrderLineRow {
  key: string;
  productId: string;
  productSku: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

const MD_CURRENCY_OPTIONS = [
  { value: 'UAH', label: 'UAH — Гривня' },
  { value: 'USD', label: 'USD — Долар' },
  { value: 'EUR', label: 'EUR — Євро' },
];

export default function PurchaseOrderEditPage({
  orderId,
  onBack,
}: PurchaseOrderEditPageProps) {
  const [mdForm] = Form.useForm();
  const [mdLines, setMdLines] = useState<OrderLineRow[]>([]);
  const [mdSupplierSearchText, setMdSupplierSearchText] = useState('');
  const [mdProductSearchText, setMdProductSearchText] = useState('');

  const { data: mdOrder, isLoading: mdIsLoadingOrder } = usePurchaseOrderById(orderId);
  const { data: mdSupplierOptions } = useSupplierSearch(mdSupplierSearchText);
  const { data: mdProductOptions } = useProductSearch(mdProductSearchText);

  const mdCreateOrder = useCreatePurchaseOrder();
  const mdUpdateOrder = useUpdatePurchaseOrder();
  const mdSubmitPO = useSubmitPO();

  // Постачальник, дата і валюта не входять до UpdatePurchaseOrderCommand — при редагуванні лише показуються
  const mdIsEditing = !!orderId;

  useEffect(() => {
    if (mdOrder) {
      mdForm.setFieldsValue({
        supplierId: mdOrder.supplierId,
        orderDate: dayjs(mdOrder.orderDate),
        expectedDeliveryDate: mdOrder.expectedDeliveryDate
          ? dayjs(mdOrder.expectedDeliveryDate)
          : undefined,
        currency: mdOrder.currency,
        exchangeRate: mdOrder.exchangeRate,
        supplierInvoiceNumber: mdOrder.supplierInvoiceNumber,
        notes: mdOrder.notes,
      });
      setMdLines(
        mdOrder.lines.map((mdLine) => ({
          key: mdLine.id,
          productId: mdLine.productId,
          productSku: mdLine.productSku,
          productName: mdLine.productName,
          quantity: mdLine.quantityOrdered,
          unitPrice: mdLine.unitPrice,
          lineTotal: mdLine.lineTotal,
        }))
      );
    }
  }, [mdOrder, mdForm]);

  const mdHandleAddLine = () => {
    setMdLines((mdPrev) => [
      ...mdPrev,
      {
        key: `new-${Date.now()}`,
        productId: '',
        productSku: '',
        productName: '',
        quantity: 1,
        unitPrice: 0,
        lineTotal: 0,
      },
    ]);
  };

  const mdHandleRemoveLine = (mdKey: string) => {
    setMdLines((mdPrev) => mdPrev.filter((mdLine) => mdLine.key !== mdKey));
  };

  const mdHandleLineChange = (
    mdKey: string,
    mdField: keyof OrderLineRow,
    mdValue: string | number
  ) => {
    setMdLines((mdPrev) =>
      mdPrev.map((mdLine) => {
        if (mdLine.key !== mdKey) return mdLine;
        const mdUpdated = { ...mdLine, [mdField]: mdValue };
        mdUpdated.lineTotal = mdUpdated.quantity * mdUpdated.unitPrice;
        return mdUpdated;
      })
    );
  };

  const mdHandleProductSelect = (mdKey: string, mdProductId: string) => {
    const mdSelected = mdProductOptions?.find((mdP) => mdP.id === mdProductId);
    if (mdSelected) {
      setMdLines((mdPrev) =>
        mdPrev.map((mdLine) =>
          mdLine.key === mdKey
            ? {
                ...mdLine,
                productId: mdSelected.id,
                productSku: mdSelected.sku,
                productName: mdSelected.name,
              }
            : mdLine
        )
      );
    }
  };

  const mdTotalAmount = useMemo(
    () => mdLines.reduce((mdSum, mdLine) => mdSum + mdLine.lineTotal, 0),
    [mdLines]
  );

  const mdExchangeRate = Form.useWatch('exchangeRate', mdForm) ?? 1;
  const mdCurrency = Form.useWatch('currency', mdForm) ?? 'UAH';

  const mdTotalUah = useMemo(
    () => mdTotalAmount * (mdCurrency === 'UAH' ? 1 : mdExchangeRate),
    [mdTotalAmount, mdExchangeRate, mdCurrency]
  );

  /** Зберігає чернетку і повертає її id (POST повертає Guid нового замовлення) */
  const mdSaveOrder = async (): Promise<string> => {
    const mdValues = await mdForm.validateFields();
    const mdLineInputs: PurchaseOrderLineInput[] = mdLines
      .filter((mdLine) => mdLine.productId)
      .map((mdLine) => ({
        id: mdLine.key.startsWith('new-') ? undefined : mdLine.key,
        productId: mdLine.productId,
        quantity: mdLine.quantity,
        unitPrice: mdLine.unitPrice,
      }));

    if (mdIsEditing && orderId) {
      await mdUpdateOrder.mutateAsync({
        request: {
          id: orderId,
          expectedDeliveryDate: mdValues.expectedDeliveryDate?.format('YYYY-MM-DD'),
          supplierInvoiceNumber: mdValues.supplierInvoiceNumber,
          notes: mdValues.notes,
          exchangeRate: mdValues.exchangeRate,
        },
        originalLines: mdOrder?.lines ?? [],
        lines: mdLineInputs,
      });
      return orderId;
    }

    const mdRequest: CreatePurchaseOrderRequest = {
      supplierId: mdValues.supplierId,
      orderDate: mdValues.orderDate.format('YYYY-MM-DD'),
      expectedDeliveryDate: mdValues.expectedDeliveryDate?.format('YYYY-MM-DD'),
      currency: mdValues.currency,
      exchangeRate: mdValues.exchangeRate,
      supplierInvoiceNumber: mdValues.supplierInvoiceNumber,
      notes: mdValues.notes,
      lines: mdLineInputs.map((mdLine) => ({
        productId: mdLine.productId,
        quantity: mdLine.quantity,
        unitPrice: mdLine.unitPrice,
      })),
    };
    return mdCreateOrder.mutateAsync(mdRequest);
  };

  const mdHandleSaveDraft = async () => {
    await mdSaveOrder();
    onBack();
  };

  const mdHandleSubmit = async () => {
    const mdSavedId = await mdSaveOrder();
    await mdSubmitPO.mutateAsync(mdSavedId);
    onBack();
  };

  const mdLineColumns: ColumnsType<OrderLineRow> = [
    {
      title: 'Продукт',
      key: 'product',
      width: 350,
      render: (_: unknown, mdRecord: OrderLineRow) => (
        <Select
          showSearch
          filterOption={false}
          placeholder="Оберіть продукт"
          value={mdRecord.productId || undefined}
          onSearch={setMdProductSearchText}
          onChange={(mdValue: string) => mdHandleProductSelect(mdRecord.key, mdValue)}
          options={mdProductOptions?.map((mdP) => ({
            value: mdP.id,
            label: `${mdP.sku} — ${mdP.name}`,
          }))}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Кількість',
      key: 'quantity',
      width: 120,
      render: (_: unknown, mdRecord: OrderLineRow) => (
        <InputNumber
          min={1}
          value={mdRecord.quantity}
          onChange={(mdValue) =>
            mdHandleLineChange(mdRecord.key, 'quantity', mdValue ?? 1)
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Ціна',
      key: 'unitPrice',
      width: 140,
      render: (_: unknown, mdRecord: OrderLineRow) => (
        <InputNumber
          min={0}
          precision={2}
          value={mdRecord.unitPrice}
          onChange={(mdValue) =>
            mdHandleLineChange(mdRecord.key, 'unitPrice', mdValue ?? 0)
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Сума',
      key: 'lineTotal',
      width: 140,
      align: 'right',
      render: (_: unknown, mdRecord: OrderLineRow) =>
        mdRecord.lineTotal.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, mdRecord: OrderLineRow) => (
        <Popconfirm
          title="Видалити рядок?"
          onConfirm={() => mdHandleRemoveLine(mdRecord.key)}
          okText="Так"
          cancelText="Ні"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  if (mdIsEditing && mdIsLoadingOrder) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {mdIsEditing ? `Замовлення ${mdOrder?.number ?? ''}` : 'Нове замовлення'}
        </Typography.Title>
      </Space>

      <Form
        form={mdForm}
        layout="vertical"
        initialValues={{
          orderDate: dayjs(),
          currency: 'UAH',
          exchangeRate: 1,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            name="supplierId"
            label="Постачальник"
            rules={[{ required: true, message: 'Оберіть постачальника' }]}
          >
            <Select
              showSearch
              filterOption={false}
              placeholder="Оберіть постачальника"
              disabled={mdIsEditing}
              onSearch={setMdSupplierSearchText}
              options={
                mdSupplierOptions?.map((mdSup) => ({
                  value: mdSup.id,
                  label: `${mdSup.code} — ${mdSup.name}`,
                })) ??
                (mdOrder ? [{ value: mdOrder.supplierId, label: mdOrder.supplierName }] : undefined)
              }
            />
          </Form.Item>
          <Form.Item
            name="orderDate"
            label="Дата замовлення"
            rules={[{ required: true, message: 'Оберіть дату' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabled={mdIsEditing} />
          </Form.Item>
          <Form.Item name="expectedDeliveryDate" label="Очікувана доставка">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: 'Оберіть валюту' }]}
          >
            <Select options={MD_CURRENCY_OPTIONS} disabled={mdIsEditing} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[{ required: true, message: 'Введіть курс' }]}
          >
            <InputNumber
              min={0.0001}
              precision={4}
              style={{ width: '100%' }}
              disabled={mdCurrency === 'UAH'}
            />
          </Form.Item>
          <Form.Item name="supplierInvoiceNumber" label="Номер рахунку постачальника">
            <Input maxLength={100} />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Примітки">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Рядки замовлення
        </Typography.Title>
        <Button icon={<PlusOutlined />} onClick={mdHandleAddLine}>
          Додати рядок
        </Button>
      </div>

      <Table<OrderLineRow>
        columns={mdLineColumns}
        dataSource={mdLines}
        rowKey="key"
        pagination={false}
        size="small"
      />

      <div
        style={{
          marginTop: 16,
          padding: '12px 16px',
          background: '#fafafa',
          borderRadius: 8,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 32,
        }}
      >
        <Typography.Text>
          Разом ({mdCurrency}):{' '}
          <Typography.Text strong>
            {mdTotalAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
          </Typography.Text>
        </Typography.Text>
        {mdCurrency !== 'UAH' && (
          <Typography.Text>
            Разом (UAH):{' '}
            <Typography.Text strong>
              {mdTotalUah.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
            </Typography.Text>
          </Typography.Text>
        )}
      </div>

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button onClick={onBack}>Скасувати</Button>
        <Button
          onClick={mdHandleSaveDraft}
          loading={mdCreateOrder.isPending || mdUpdateOrder.isPending}
        >
          Зберегти чернетку
        </Button>
        <Button
          type="primary"
          onClick={mdHandleSubmit}
          loading={mdSubmitPO.isPending || mdCreateOrder.isPending || mdUpdateOrder.isPending}
        >
          Подати на затвердження
        </Button>
      </div>
    </>
  );
}
